'use client';
import { useGdprConsent } from '#pplx/frontend/analytics/browser/useGdprConsent.js';
import { PERPLEXITY_APP_PROTOCOL } from '#pplx/frontend/common/protocols.js';
import { setCookieHasClickedGetApp } from '#pplx/frontend/cookies/metadataCookieManager.js';
import { getVisitorIdCookie } from '#pplx/frontend/cookies/sessionCookieManager.js';
import { useSingularAttribution } from '#pplx/frontend/growth/analytics-singular/SingularAttributionProviderShared.js';
import { redirectWindow } from '#pplx/frontend/platform-components/redirectWindow.js';
import { usePathname } from '#pplx/frontend/router/usePathname.js';
import { useCallback, useEffect, useMemo, useState } from 'react';
// singular lets us propagate web-based visitor ID cookies to
// app store downloads, so we can attribute installs to web visitors
const MWEB_TO_APP_LINK_PREFIX = 'https://perplexity.sng.link/A6awk/ppas?_smtype=3&pvid=';
export const ONBOARDING_TO_APP_LINK_PREFIX = 'https://perplexity.sng.link/A6awk/rcv6y?_smtype=3&pvid=';
export const useSingularAttributionAppLink = ({ origin, linkPrefix = MWEB_TO_APP_LINK_PREFIX, isCanonicalDeeplink = false, deepLink: configuredDeepLink, }) => {
    const visitorId = getVisitorIdCookie();
    const { sendAppDownloadClickedEventSingular } = useSingularAttribution();
    const { hasConsent } = useGdprConsent();
    const pathname = usePathname();
    const attributionVisitorId = hasConsent ? visitorId : undefined;
    const defaultPassthroughParams = useMemo(() => {
        const params = { origin };
        if (attributionVisitorId)
            params.pvid = attributionVisitorId;
        if (pathname)
            params.pathname = pathname;
        return params;
    }, [attributionVisitorId, origin, pathname]);
    const defaultDeepLink = useMemo(() => configuredDeepLink ?? getInferredDeepLink({ pathname, isCanonicalDeeplink }), [configuredDeepLink, isCanonicalDeeplink, pathname]);
    const [downloadAppLink, setDownloadAppLink] = useState(() => buildSingularBaseLink({
        linkPrefix,
        visitorId: attributionVisitorId,
        deepLink: defaultDeepLink,
    }));
    const buildAppDownloadLink = useCallback(async (params = {}) => {
        const deepLink = params.deepLink ?? defaultDeepLink;
        const deferredDeepLink = params.deferredDeepLink ?? deepLink;
        const baseLink = buildSingularBaseLink({
            linkPrefix,
            visitorId: attributionVisitorId,
            deepLink,
        });
        const passthroughParams = buildPassthroughParams({
            defaultPassthroughParams,
            passthroughPayload: params.passthroughPayload,
        });
        // `hasConsent` already accounts for GPC (see useGdprConsent).
        if (!attributionVisitorId) {
            return baseLink;
        }
        const { singularSdk } = await import('singular-sdk');
        while (!singularSdk._isInitialized) {
            // initialization is in SingularAttribution.tsx
            await new Promise((resolve) => setTimeout(resolve, 100));
        }
        return singularSdk.buildWebToAppLink(baseLink, deepLink, passthroughParams, deferredDeepLink);
    }, [attributionVisitorId, defaultDeepLink, defaultPassthroughParams, linkPrefix]);
    useEffect(() => {
        let isMounted = true;
        const fetchLink = async () => {
            const link = await buildAppDownloadLink();
            if (isMounted) {
                setDownloadAppLink(link);
            }
        };
        fetchLink();
        return () => {
            isMounted = false;
        };
    }, [buildAppDownloadLink]);
    const handleAppDownloadLinkClicked = useCallback(() => {
        setCookieHasClickedGetApp();
        sendAppDownloadClickedEventSingular(origin);
    }, [origin, sendAppDownloadClickedEventSingular]);
    const handleAppDownloadClicked = useCallback(() => {
        handleAppDownloadLinkClicked();
        redirectWindow(downloadAppLink, 'Singular download app');
    }, [downloadAppLink, handleAppDownloadLinkClicked]);
    return useMemo(() => ({
        downloadAppLink,
        buildAppDownloadLink,
        handleAppDownloadLinkClicked,
        handleAppDownloadClicked,
    }), [buildAppDownloadLink, downloadAppLink, handleAppDownloadClicked, handleAppDownloadLinkClicked]);
};
function getInferredDeepLink({ pathname, isCanonicalDeeplink, }) {
    if (!pathname)
        return null;
    const trimmedPathname = pathname.startsWith('/') ? pathname.slice(1) : pathname;
    return isCanonicalDeeplink
        ? `${PERPLEXITY_APP_PROTOCOL}//canonical-page?url=https://www.perplexity.ai/app/${trimmedPathname}`
        : `${PERPLEXITY_APP_PROTOCOL}//${trimmedPathname}`;
}
function buildSingularBaseLink({ linkPrefix, visitorId, deepLink, }) {
    const url = new URL(getLinkWithVisitorId({ linkPrefix, visitorId }));
    url.searchParams.set('_ios_dl', deepLink ?? '');
    url.searchParams.set('_android_dl', deepLink ?? '');
    return url.toString();
}
function buildPassthroughParams({ defaultPassthroughParams, passthroughPayload, }) {
    if (passthroughPayload) {
        return JSON.stringify({ ...defaultPassthroughParams, ...passthroughPayload });
    }
    const params = new URLSearchParams();
    Object.entries(defaultPassthroughParams).forEach(([key, value]) => {
        params.set(key, value);
    });
    const passthroughParams = params.toString();
    return passthroughParams || null;
}
function getLinkWithVisitorId({ linkPrefix, visitorId, }) {
    if (!visitorId) {
        const url = new URL(linkPrefix);
        url.searchParams.delete('pvid');
        return url.toString();
    }
    if (!linkPrefix.endsWith('='))
        return linkPrefix;
    return `${linkPrefix}${visitorId}`;
}
//# sourceMappingURL=useSingularAttributionAppLink.js.map